import { Badge, Button, Card, CardHeader, Drawer, EmptyState, formatDate, Skeleton, Table, useToast, type Column } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getBill, recordPayment, voidBill, type Bill, type BillLine, type Tender } from "../../api/billing";
import { isApiError } from "../../api/client";
import { createCreditNote, getBillInvoice } from "../../api/invoices";
import { createIntent, listNeedsReview, resolveIntent } from "../../api/payments";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { useAppContext, useIdempotentKey, useMoney, useScope } from "./hooks";
import { fractionDigitsOf, parseMoneyInput } from "./money";
import { payTone } from "./BillingPage";
import { PageHead, QueryFailure } from "./states";
import { IntentDrawer } from "./UpiDrawer";
import { billing as t } from "./strings";
import s from "./billing.module.css";

const msg = (e: unknown) => (isApiError(e) ? e.message : t.errors.generic);

export function BillDetailPage() {
  const { id = "" } = useParams();
  const ctx = useAppContext();
  const { client, scopeKey } = useScope();
  const { fmt, currency, locale } = useMoney(ctx);
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["bill", ...scopeKey, id], queryFn: () => getBill(client, id), retry: false });
  const invoice = useQuery({ queryKey: ["bill-invoice", ...scopeKey, id], queryFn: () => getBillInvoice(client, id), retry: false, enabled: q.isSuccess });
  const [dialog, setDialog] = useState<null | "collect" | "void" | "credit">(null);
  const [intentId, setIntentId] = useState<string | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["bill", ...scopeKey, id] }); void qc.invalidateQueries({ queryKey: ["bills"] }); void qc.invalidateQueries({ queryKey: ["bill-invoice"] }); };

  const intentKeys = useIdempotentKey();
  // Held here, not in the drawers: closing a drawer after a dropped response must not lose the undecided key.
  const collectKeys = useIdempotentKey();
  const creditKeys = useIdempotentKey();
  const startIntent = useMutation({
    mutationFn: (method: "upi_qr" | "payment_link") => createIntent(client, id, { method }, intentKeys.forBody(method)),
    onSuccess: (r) => { intentKeys.renew(); setIntentId(r.intent.id); },
    onError: (e) => toast.show(msg(e)),
  });
  const reviews = useQuery({
    queryKey: ["intent-review", ...scopeKey, id],
    queryFn: () => listNeedsReview(client, id),
    enabled: q.isSuccess && ctx.has("revenue.resolve_payment_review"), retry: false,
  });
  const resolve = useMutation({
    mutationFn: (v: { id: string; action: "apply" | "reject" }) => resolveIntent(client, v.id, v.action),
    onSuccess: () => { toast.show(t.bill.resolved); void reviews.refetch(); refresh(); },
    onError: (e) => toast.show(msg(e)),
  });

  if (ctx.status !== "loading" && !areaAllows("billing", ctx.has)) return <NoAccess />;
  if (q.isPending) return <div className={s.page}><Card><div className={s.skel} aria-busy="true"><Skeleton height={28} /><Skeleton height={120} /><Skeleton height={60} /></div></Card></div>;
  if (q.error) return <div className={s.page}><QueryFailure error={q.error} what="this bill" missing="Bill not found" onRetry={() => void q.refetch()} /></div>;
  const b = q.data;
  const rev = ctx.has("revenue.bill_amounts");
  const open = b.status === "issued" && b.balance > 0;
  const state = b.status === "void" ? "void" : b.paymentStatus;
  const canPay = ctx.has("billing.collect_payment") && open;
  const cols: Column<BillLine>[] = [
    { key: "n", header: "Item", cell: (l) => <span className={s.wrap2}>{l.name}</span> },
    { key: "q", header: t.invoice.qty, align: "end", cell: (l) => l.quantity },
    { key: "t", header: t.invoice.total, align: "end", cell: (l) => <span className={s.num}>{fmt(l.lineTotal)}</span> },
  ];
  return (
    <div className={s.page}>
      <title>Bill · Helmr</title>
      <PageHead title={`Bill ${b.id.slice(-6).toUpperCase()}`} subtitle={`${b.buyerName ?? "Walk-in"} · ${formatDate(b.businessDate, ctx.locale, "full")}`}
        action={<Button as={Link} to="/billing">{t.bill.back}</Button>} />
      <Card>
        <CardHeader title={<>{t.bill.status} <Badge tone={payTone(state)} dot>{t.payStatus[state]}</Badge></>}
          subtitle={b.status === "void" ? `${b.voidedAt ? t.bill.voidedOn(new Intl.DateTimeFormat(ctx.locale, { dateStyle: "medium", timeZone: ctx.timeZone }).format(b.voidedAt)) : ""}${b.voidReason ? `: ${b.voidReason}` : ""}` : undefined} />
        <Table label={t.bill.lines} columns={cols} rows={b.lines} rowKey={(l) => l.id} />
        <div className={s.totals}>
          <div className={s.trow}><span>{t.counter.summary.subtotal}</span><span className={s.num}>{fmt(b.subtotalExTax)}</span></div>
          {b.discountTotal > 0 ? <div className={s.trow}><span>{t.counter.summary.discount}</span><span className={s.num}>{fmt(b.discountTotal)}</span></div> : null}
          <div className={s.trow}><span>{t.counter.summary.tax}</span><span className={s.num}>{fmt(b.taxTotal)}</span></div>
          {b.roundOff !== 0 ? <div className={s.trow}><span>{t.counter.summary.round}</span><span className={s.num}>{fmt(b.roundOff)}</span></div> : null}
          <div className={`${s.trow} ${s.grand}`}><span>{t.counter.summary.total}</span><span className={s.num}>{fmt(b.total)}</span></div>
          <div className={s.trow}><span>Paid</span><span className={s.num}>{fmt(b.paidTotal)}</span></div>
          <div className={s.trow}><span>{t.cols.balance}</span><span className={s.num} data-testid="bill-balance">{fmt(b.balance)}</span></div>
        </div>
        <div className={s.actions}>
          {canPay ? <Button variant="primary" onClick={() => setDialog("collect")}>{t.bill.collect}</Button> : null}
          {canPay ? <Button onClick={() => startIntent.mutate("upi_qr")} busy={startIntent.isPending}>{t.bill.upi}</Button> : null}
          {canPay ? <Button onClick={() => startIntent.mutate("payment_link")} busy={startIntent.isPending}>{t.bill.link}</Button> : null}
          {invoice.data ? <Button as={Link} to={`/billing/invoices/${invoice.data.id}`}>{t.bill.print}</Button> : null}
          {b.status === "issued" && invoice.data && ctx.has("billing.credit_note") ? <Button onClick={() => setDialog("credit")}>{t.bill.credit}</Button> : null}
          {b.status === "issued" && ctx.has("revenue.void_bill") ? <Button onClick={() => setDialog("void")}>{t.bill.void}</Button> : null}
        </div>
      </Card>

      {reviews.data && reviews.data.length > 0 ? (
        <Card>
          <CardHeader title={t.bill.review} subtitle={t.bill.reviewBody} />
          {reviews.data.map((i) => (
            <div key={i.id} className={s.dueRow}>
              <span>{t.methods[i.method]}{rev ? ` · ${fmt(i.amount)}` : ""}</span>
              <span className={s.dueBtns}>
                <Button size="sm" onClick={() => resolve.mutate({ id: i.id, action: "apply" })} busy={resolve.isPending}>{t.bill.apply}</Button>
                <Button size="sm" onClick={() => resolve.mutate({ id: i.id, action: "reject" })} busy={resolve.isPending}>{t.bill.reject}</Button>
              </span>
            </div>
          ))}
        </Card>
      ) : null}

      <Card>
        <CardHeader title={t.bill.payments} />
        {b.payments.length === 0 ? <EmptyState compact icon="cash" title={t.bill.noPayments} /> : b.payments.map((p) => (
          <div key={p.id} className={s.dueRow}><span>{t.methods[p.method] ?? p.method} · {formatDate(p.businessDate, ctx.locale, "short")}</span><span className={s.num}>{fmt(p.amount)}</span></div>
        ))}
      </Card>
      {b.dues.length > 0 ? (
        <Card>
          <CardHeader title={t.bill.dues} />
          {b.dues.map((d) => <div key={d.seq} className={s.dueRow}><span>{formatDate(d.dueDate, ctx.locale, "short")}</span><span className={s.num}>{fmt(d.amount)}</span></div>)}
        </Card>
      ) : null}

      {dialog === "collect" ? <CollectDrawer bill={b} idem={collectKeys} currency={currency} locale={locale} onClose={(done) => { setDialog(null); if (done) refresh(); }} /> : null}
      {dialog === "void" ? <VoidDrawer bill={b} onClose={(done) => { setDialog(null); if (done) refresh(); }} /> : null}
      {dialog === "credit" ? <CreditDrawer bill={b} idem={creditKeys} onClose={(done) => { setDialog(null); if (done) refresh(); }} /> : null}
      {intentId ? <IntentDrawer intentId={intentId} onClose={(settled) => { setIntentId(null); refresh(); if (settled) toast.show(t.bill.paid); }} /> : null}
    </div>
  );
}

function CollectDrawer({ bill, idem, currency, locale, onClose }: { bill: Bill; idem: ReturnType<typeof useIdempotentKey>; currency: string; locale: string; onClose: (done: boolean) => void }) {
  const { client } = useScope();
  const ctx = useAppContext();
  const { fmt } = useMoney(ctx);
  const toast = useToast();
  const [method, setMethod] = useState<Tender>("cash");
  const [full, setFull] = useState(true);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const digits = fractionDigitsOf(currency, locale);
  const amount = full ? bill.balance : parseMoneyInput(text, digits);
  const m = useMutation({
    mutationFn: () => recordPayment(client, bill.id, { method, amount: amount! }, idem.forBody(`${method}:${amount}`)),
    onSuccess: () => { idem.renew(); toast.show(t.bill.paid); onClose(true); },
    onError: (e) => { idem.setPending(isApiError(e) && e.isOffline); setError(msg(e)); },
  });
  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose(false); }} title={t.bill.collectTitle} description={`${t.cols.balance}: ${fmt(bill.balance)}`}
      footer={<Button variant="primary" disabled={amount === null || amount === undefined} busy={m.isPending} busyLabel="Recording…" onClick={() => { if (!m.isPending) m.mutate(); }}>{t.bill.collect}</Button>}>
      <div className={s.pad}>
        <label className={s.field}>{t.counter.tender}
          <select value={method} onChange={(e) => setMethod(e.target.value as Tender)}>
            <option value="cash">{t.methods.cash}</option><option value="member_credit">{t.methods.member_credit}</option>
          </select>
        </label>
        <div className={s.payrow}>
          <Button size="sm" variant={full ? "primary" : "default"} onClick={() => setFull(true)}>{t.counter.payFull}: {fmt(bill.balance)}</Button>
          <Button size="sm" variant={full ? "default" : "primary"} onClick={() => setFull(false)}>Other amount</Button>
        </div>
        {full ? null : (
          <label className={s.field}>{t.counter.payAmount}
            <input inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} aria-invalid={text !== "" && amount === null ? true : undefined} />
          </label>
        )}
        {!full && text !== "" && amount === null ? <span className={s.err} role="alert">{t.counter.payBad}</span> : null}
        {error ? <span className={s.err} role="alert">{error}</span> : null}
      </div>
    </Drawer>
  );
}

function VoidDrawer({ bill, onClose }: { bill: Bill; onClose: (done: boolean) => void }) {
  const { client } = useScope();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => voidBill(client, bill.id, reason.trim()),
    onSuccess: () => { toast.show(t.bill.voided); onClose(true); },
    onError: (e) => setError(msg(e)),
  });
  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose(false); }} title={t.bill.voidTitle} description={t.bill.voidBody}
      footer={<Button variant="primary" disabled={reason.trim() === ""} busy={m.isPending} busyLabel="Voiding…" onClick={() => m.mutate()}>{t.bill.voidConfirm}</Button>}>
      <div className={s.pad}>
        <label className={s.field}>{t.bill.voidReason}<textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        {error ? <span className={s.err} role="alert">{error}</span> : null}
      </div>
    </Drawer>
  );
}

function CreditDrawer({ bill, idem, onClose }: { bill: Bill; idem: ReturnType<typeof useIdempotentKey>; onClose: (done: boolean) => void }) {
  const { client } = useScope();
  const ctx = useAppContext();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [settlement, setSettlement] = useState<"reduce_balance" | "member_credit" | "refund">("reduce_balance");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const leaves = bill.lines.filter((l) => !bill.lines.some((o) => o.parentLineId === l.id));
  const body = { billId: bill.id, lines: [...picked].map((billLineId) => ({ billLineId })), reason: reason.trim(), settlement, ...(settlement === "refund" ? { refundMethod: "cash" as const } : {}) };
  const m = useMutation({
    mutationFn: () => createCreditNote(client, body, idem.forBody(JSON.stringify(body))),
    onSuccess: () => { idem.renew(); toast.show(t.bill.creditDone); onClose(true); },
    onError: (e) => { idem.setPending(isApiError(e) && e.isOffline); setError(msg(e)); },
  });
  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose(false); }} title={t.bill.creditTitle} description={t.bill.creditLines}
      footer={<Button variant="primary" disabled={picked.size === 0 || reason.trim() === ""} busy={m.isPending} busyLabel="Issuing…" onClick={() => { if (!m.isPending) m.mutate(); }}>{t.bill.creditConfirm}</Button>}>
      <div className={s.pad}>
        {leaves.map((l) => (
          <label key={l.id} className={s.payrow}>
            <input type="checkbox" checked={picked.has(l.id)} onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(l.id); else n.delete(l.id); return n; })} />
            <span>{l.name}</span>
          </label>
        ))}
        <label className={s.field}>{t.bill.creditSettle}
          <select value={settlement} onChange={(e) => setSettlement(e.target.value as typeof settlement)}>
            <option value="reduce_balance">{t.settlements.reduce_balance}</option>
            <option value="member_credit">{t.settlements.member_credit}</option>
            {ctx.has("revenue.refund_settlement") ? <option value="refund">{t.settlements.refund}</option> : null}
          </select>
        </label>
        <label className={s.field}>{t.bill.voidReason}<textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        {error ? <span className={s.err} role="alert">{error}</span> : null}
      </div>
    </Drawer>
  );
}
